// ============================================
// MEDFLOW - Boxes (Treatment Rooms) Page
// Full CRUD: Create, Edit, Delete, Schedule
// ============================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DoorOpen, Clock, Plus, Search, Edit, Trash2,
  CheckCircle, XCircle, ChevronDown, ChevronUp,
  Calendar, X, Save, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { boxesApi } from '@/lib/api';
import { isSupabaseAuthEnabled } from '@/lib/auth-config';
import {
  createSupabaseBox,
  loadSupabaseBoxesWithSchedules,
  updateSupabaseBox,
  updateSupabaseBoxSchedule,
} from '@/lib/supabase-data';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

// ============ TYPES ============

interface BoxSchedule {
  id: string;
  dayOfWeek: number;
  startTime: string | null;
  endTime: string | null;
  startTime2: string | null;
  endTime2: string | null;
  isOpen: boolean;
}

interface Box {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  schedules: BoxSchedule[];
}

interface ScheduleFormItem {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  startTime2: string;
  endTime2: string;
  isOpen: boolean;
}

// ============ CONSTANTS ============

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DAY_NAMES_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DAYS_ORDERED = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun
const boxPalette = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

// ============ MODAL OVERLAY ============

function Modal({ open, onClose, title, children, wide }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={cn(
        'relative bg-white rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto',
        wide ? 'w-full max-w-2xl' : 'w-full max-w-md'
      )}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============ CREATE / EDIT BOX FORM ============

function BoxForm({ box, onSubmit, onCancel, isLoading }: {
  box?: Box | null;
  onSubmit: (data: { name: string; description?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(box?.name || '');
  const [description, setDescription] = useState(box?.description || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim() || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Box 1, Sala A..."
          className="w-full h-10 px-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          required
          minLength={2}
          maxLength={100}
          autoFocus
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descripción opcional del box..."
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary text-sm resize-none"
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!name.trim() || isLoading}>
          <Save className="w-4 h-4 mr-1" />
          {isLoading ? 'Guardando...' : box ? 'Actualizar' : 'Crear Box'}
        </Button>
      </div>
    </form>
  );
}

// ============ SCHEDULE EDITOR ============

function ScheduleEditor({ box, onSubmit, onCancel, isLoading }: {
  box: Box;
  onSubmit: (schedule: ScheduleFormItem[]) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [schedule, setSchedule] = useState<ScheduleFormItem[]>(() => {
    return DAYS_ORDERED.map((day) => {
      const existing = box.schedules.find(s => s.dayOfWeek === day);
      return {
        dayOfWeek: day,
        startTime: existing?.startTime || '09:00',
        endTime: existing?.endTime || '18:00',
        startTime2: existing?.startTime2 || '',
        endTime2: existing?.endTime2 || '',
        isOpen: existing?.isOpen ?? (day >= 1 && day <= 5),
      };
    });
  });

  const updateDay = (dayOfWeek: number, field: keyof ScheduleFormItem, value: string | boolean) => {
    setSchedule(prev => prev.map(s =>
      s.dayOfWeek === dayOfWeek ? { ...s, [field]: value } : s
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(schedule);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-sm text-gray-500 mb-2">
        Configura los horarios de apertura para <strong>{box.name}</strong>.
      </p>
      <div className="space-y-2">
        {schedule.map((day) => (
          <div key={day.dayOfWeek} className={cn(
            'flex items-center gap-3 p-3 rounded-lg border transition-colors',
            day.isOpen ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'
          )}>
            {/* Day toggle */}
            <label className="flex items-center gap-2 w-28 shrink-0 cursor-pointer">
              <input
                type="checkbox"
                checked={day.isOpen}
                onChange={(e) => updateDay(day.dayOfWeek, 'isOpen', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className={cn('text-sm font-medium', day.isOpen ? 'text-gray-800' : 'text-gray-400')}>
                {DAY_NAMES[day.dayOfWeek]}
              </span>
            </label>

            {day.isOpen ? (
              <div className="flex items-center gap-2 flex-1 flex-wrap">
                {/* Shift 1 */}
                <div className="flex items-center gap-1">
                  <input
                    type="time"
                    value={day.startTime}
                    onChange={(e) => updateDay(day.dayOfWeek, 'startTime', e.target.value)}
                    className="h-8 px-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-gray-400 text-xs">a</span>
                  <input
                    type="time"
                    value={day.endTime}
                    onChange={(e) => updateDay(day.dayOfWeek, 'endTime', e.target.value)}
                    className="h-8 px-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                {/* Shift 2 (optional) */}
                <div className="flex items-center gap-1">
                  <span className="text-gray-300 text-xs">|</span>
                  <input
                    type="time"
                    value={day.startTime2}
                    onChange={(e) => updateDay(day.dayOfWeek, 'startTime2', e.target.value)}
                    placeholder="--:--"
                    className="h-8 px-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-gray-400 text-xs">a</span>
                  <input
                    type="time"
                    value={day.endTime2}
                    onChange={(e) => updateDay(day.dayOfWeek, 'endTime2', e.target.value)}
                    placeholder="--:--"
                    className="h-8 px-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            ) : (
              <span className="text-sm text-gray-400 italic">Cerrado</span>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          <Save className="w-4 h-4 mr-1" />
          {isLoading ? 'Guardando...' : 'Guardar horarios'}
        </Button>
      </div>
    </form>
  );
}

// ============ DELETE CONFIRMATION ============

function DeleteConfirm({ box, onConfirm, onCancel, isLoading }: {
  box: Box;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <p className="text-gray-700">
            ¿Estás seguro de que quieres desactivar <strong>{box.name}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-1">
            El box será desactivado y no aparecerá en la lista por defecto.
            Podrás reactivarlo más tarde.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          disabled={isLoading}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          {isLoading ? 'Desactivando...' : 'Desactivar'}
        </Button>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============

export function BoxesPage() {
  const { clinic } = useAuthStore();
  const [expandedBox, setExpandedBox] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBox, setEditingBox] = useState<Box | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<Box | null>(null);
  const [deletingBox, setDeletingBox] = useState<Box | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['boxes', clinic?.id, showInactive, isSupabaseAuthEnabled ? 'supabase' : 'legacy'],
    queryFn: async () => {
      if (isSupabaseAuthEnabled && clinic?.id) {
        return loadSupabaseBoxesWithSchedules(clinic.id, showInactive ? 'inactive' : 'active');
      }

      const response = await boxesApi.list(showInactive ? false : undefined);
      return (response.data as Box[]) || [];
    },
    enabled: !isSupabaseAuthEnabled || Boolean(clinic?.id),
  });

  // ---- Mutations ----
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      if (isSupabaseAuthEnabled && clinic?.id) {
        return createSupabaseBox(clinic.id, data);
      }

      await boxesApi.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      setShowCreateModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; description?: string } }) => {
      if (isSupabaseAuthEnabled) {
        await updateSupabaseBox(id, data);
        return;
      }

      await boxesApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      setEditingBox(null);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      if (isSupabaseAuthEnabled) {
        await updateSupabaseBox(id, { isActive });
        return;
      }

      await boxesApi.update(id, { isActive });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['boxes'] }),
  });

  const scheduleMutation = useMutation({
    mutationFn: async ({ id, schedule }: { id: string; schedule: ScheduleFormItem[] }) => {
      const normalizedSchedule = schedule.map(s => ({
        dayOfWeek: s.dayOfWeek,
        startTime: s.isOpen ? s.startTime : undefined,
        endTime: s.isOpen ? s.endTime : undefined,
        startTime2: s.isOpen && s.startTime2 ? s.startTime2 : undefined,
        endTime2: s.isOpen && s.endTime2 ? s.endTime2 : undefined,
        isOpen: s.isOpen,
      }));

      if (isSupabaseAuthEnabled) {
        await updateSupabaseBoxSchedule(id, normalizedSchedule);
        return;
      }

      await boxesApi.updateSchedule(id, normalizedSchedule);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      setEditingSchedule(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (isSupabaseAuthEnabled) {
        await updateSupabaseBox(id, { isActive: false });
        return;
      }

      await boxesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boxes'] });
      setDeletingBox(null);
    },
  });

  const boxes = data || [];

  const filteredBoxes = boxes.filter((box) => {
    const s = search.toLowerCase();
    if (!s) return true;
    return box.name.toLowerCase().includes(s) || box.description?.toLowerCase().includes(s);
  });

  const activeCount = boxes.filter(b => b.isActive).length;
  const inactiveCount = boxes.filter(b => !b.isActive).length;

  const toggleExpand = (id: string) => {
    setExpandedBox(expandedBox === id ? null : id);
  };

  const formatTimeRange = (start: string | null, end: string | null): string => {
    if (!start || !end) return '';
    return `${start} - ${end}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Boxes (Salas)</h1>
          <p className="text-gray-600">
            {boxes.length} box{boxes.length !== 1 ? 'es' : ''} configurado{boxes.length !== 1 ? 's' : ''}
            {' · '}
            {activeCount} activo{activeCount !== 1 ? 's' : ''}
            {inactiveCount > 0 && ` · ${inactiveCount} inactivo${inactiveCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            Ver inactivos
          </label>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Agregar Box
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar boxes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <div className="h-48 bg-gray-100 animate-pulse rounded-xl" />
            </Card>
          ))}
        </div>
      ) : filteredBoxes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <DoorOpen className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500 text-center">
              {search ? 'No se encontraron boxes con ese nombre' : 'No hay boxes configurados'}
            </p>
            {!search && (
              <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Agregar Box
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredBoxes.map((box, index) => {
            const color = boxPalette[index % boxPalette.length];
            const isExpanded = expandedBox === box.id;
            const sortedSchedules = [...(box.schedules || [])].sort((a, b) => a.dayOfWeek - b.dayOfWeek);

            return (
              <Card key={box.id} className={cn('transition-shadow hover:shadow-md', !box.isActive && 'opacity-60')}>
                {/* Header with color bar */}
                <div className="h-2 rounded-t-xl" style={{ backgroundColor: color }} />
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                        <DoorOpen className="w-5 h-5" style={{ color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{box.name}</h3>
                        {box.description && <p className="text-sm text-gray-500">{box.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge className={box.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                        {box.isActive ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                  </div>

                  {/* Compact schedule preview */}
                  <div className="mt-4 flex gap-1">
                    {DAYS_ORDERED.map((day) => {
                      const sch = sortedSchedules.find(s => s.dayOfWeek === day);
                      const isOpen = sch?.isOpen ?? false;
                      return (
                        <div
                          key={day}
                          className={cn(
                            'flex-1 text-center py-1.5 rounded text-xs font-medium',
                            isOpen
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-50 text-gray-400'
                          )}
                          title={`${DAY_NAMES[day]}: ${isOpen ? 'Abierto' : 'Cerrado'}`}
                        >
                          {DAY_NAMES_SHORT[day]?.charAt(0)}
                        </div>
                      );
                    })}
                  </div>

                  {/* Action buttons */}
                  <div className="mt-4 flex items-center justify-between">
                    <button
                      onClick={() => toggleExpand(box.id)}
                      className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      <Clock className="w-4 h-4" />
                      Ver horarios
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingSchedule(box)}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar horarios"
                      >
                        <Calendar className="w-4 h-4 text-blue-500" />
                      </button>
                      <button
                        onClick={() => setEditingBox(box)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Editar box"
                      >
                        <Edit className="w-4 h-4 text-gray-400" />
                      </button>
                      {box.isActive ? (
                        <button
                          onClick={() => setDeletingBox(box)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Desactivar box"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActiveMutation.mutate({ id: box.id, isActive: true })}
                          className="text-xs text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" />Activar
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded schedule details */}
                  {isExpanded && (
                    <div className="mt-4 border-t pt-4 space-y-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Horarios semanales</h4>
                      {DAYS_ORDERED.map((day) => {
                        const sch = sortedSchedules.find(s => s.dayOfWeek === day);
                        const isOpen = sch?.isOpen ?? false;

                        return (
                          <div key={day} className={cn(
                            'flex items-center justify-between py-2 px-3 rounded-lg text-sm',
                            isOpen ? 'bg-white' : 'bg-gray-50'
                          )}>
                            <span className={cn('font-medium w-24', isOpen ? 'text-gray-800' : 'text-gray-400')}>
                              {DAY_NAMES[day]}
                            </span>
                            {isOpen ? (
                              <div className="flex items-center gap-2 flex-1 justify-end">
                                <span className="text-gray-700">
                                  {formatTimeRange(sch?.startTime ?? null, sch?.endTime ?? null)}
                                </span>
                                {sch?.startTime2 && sch?.endTime2 && (
                                  <>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-gray-700">
                                      {formatTimeRange(sch.startTime2, sch.endTime2)}
                                    </span>
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Cerrado</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <DoorOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{boxes.length}</p>
                <p className="text-sm text-gray-500">Total boxes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeCount}</p>
                <p className="text-sm text-gray-500">Boxes activos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inactiveCount}</p>
                <p className="text-sm text-gray-500">Boxes inactivos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========== MODALS ========== */}

      {/* Create Box Modal */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} title="Crear nuevo Box">
        <BoxForm
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setShowCreateModal(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      {/* Edit Box Modal */}
      <Modal open={!!editingBox} onClose={() => setEditingBox(null)} title="Editar Box">
        {editingBox && (
          <BoxForm
            box={editingBox}
            onSubmit={(data) => updateMutation.mutate({ id: editingBox.id, data })}
            onCancel={() => setEditingBox(null)}
            isLoading={updateMutation.isPending}
          />
        )}
      </Modal>

      {/* Edit Schedule Modal */}
      <Modal open={!!editingSchedule} onClose={() => setEditingSchedule(null)} title="Editar Horarios" wide>
        {editingSchedule && (
          <ScheduleEditor
            box={editingSchedule}
            onSubmit={(schedule) => scheduleMutation.mutate({ id: editingSchedule.id, schedule })}
            onCancel={() => setEditingSchedule(null)}
            isLoading={scheduleMutation.isPending}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={!!deletingBox} onClose={() => setDeletingBox(null)} title="Desactivar Box">
        {deletingBox && (
          <DeleteConfirm
            box={deletingBox}
            onConfirm={() => deleteMutation.mutate(deletingBox.id)}
            onCancel={() => setDeletingBox(null)}
            isLoading={deleteMutation.isPending}
          />
        )}
      </Modal>
    </div>
  );
}
